export type ImageSourceReason =
    | 'data-image'
    | 'same-origin'
    | 'approved-host'
    | 'external-host'
    | 'insecure-scheme'
    | 'protocol-relative'
    | 'unsupported-scheme'
    | 'malformed'
    | 'empty';

export interface ImageSourceDecision {
    kind: 'allow' | 'block';
    reason: ImageSourceReason;
}

const RASTER_DATA_URL = /^data:image\/(?:png|jpeg|gif|webp);base64,[a-z0-9+/]*={0,2}$/i;
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const BARE_HOSTNAME = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/;

const allow = (reason: ImageSourceReason): ImageSourceDecision => ({ kind: 'allow', reason });
const block = (reason: ImageSourceReason): ImageSourceDecision => ({ kind: 'block', reason });

/** Parses a comma-separated list of bare host names, dropping anything that is not a bare host name. */
export const parseApprovedImageHosts = (raw: string | undefined): readonly string[] => {
    if (!raw) {
        return [];
    }
    return raw
        .split(',')
        .map((host) => host.trim().toLowerCase())
        .filter((host) => BARE_HOSTNAME.test(host));
};

/** Decides whether an image source may be rendered as an element. Pure: no I/O, no global state. */
export const classifyImageSource = (src: string | undefined, approvedHosts: readonly string[]): ImageSourceDecision => {
    const value = src?.trim();
    if (!value) {
        return block('empty');
    }
    if (CONTROL_CHARACTERS.test(value)) {
        return block('malformed');
    }

    // Browsers read a backslash as a slash, so "/\host" is protocol-relative.
    if (value.startsWith('//') || value.startsWith('/\\') || value.startsWith('\\')) {
        return block('protocol-relative');
    }
    if (value.startsWith('/')) {
        return allow('same-origin');
    }

    const scheme = SCHEME.exec(value)?.[1].toLowerCase();
    if (!scheme) {
        return block('malformed');
    }
    if (scheme === 'data') {
        return RASTER_DATA_URL.test(value) ? allow('data-image') : block('unsupported-scheme');
    }
    if (scheme === 'http') {
        return block('insecure-scheme');
    }
    if (scheme !== 'https') {
        return block('unsupported-scheme');
    }

    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return block('malformed');
    }
    if (url.username || url.password || url.port) {
        return block('external-host');
    }
    return approvedHosts.includes(url.hostname) ? allow('approved-host') : block('external-host');
};
